import { useEffect, useRef, useState } from 'react'

import { EMPTY_FUNCTION, EMPTY_STRING } from '../../constants/common'
import { SUBMENU_DEFAULT_VALUE } from '../../constants/route'
import { getEmptyPermanentFilters } from '../../utils/filter'
import { toOrderingList } from '../../utils/sort'
import { useAsyncCallback } from '../useAsyncCallback'
import { useFilterStateLoader } from '../useFilterStateLoader'
import { useIsMountedRef } from '../useIsMountedRef'
import { useNotification } from '../useNotification'
import { useTableConfigLoader } from '../useTableConfigLoader'

import { updateOldApiListTableConfig } from './useOldApiListTable.api'
import {
  getOldApiListPageSizePagination,
  getOldApiListPagination,
  getOldApiListParams,
  getOldApiListPreviousPagination,
  getOldApiListTableKey,
  isOldApiListPageMissing,
  isOldApiListTableKeyShown,
  isOldApiListTableLatestLoad,
  isOldApiListTableLoading,
  updateOldApiListTableItem,
} from './useOldApiListTable.utils'

export function useOldApiListTable({
  entity,
  getList,
  listKey = EMPTY_STRING,
  pagination,
  setPageSize,
  setPagination,
  onLoad = EMPTY_FUNCTION,
}) {
  const [items, setItems] = useState(null)
  const [isLoadingPage, setLoadingPage] = useState(false)
  const [isInitiated, setInitiated] = useState(false)
  const [isPageLoadFailed, setPageLoadFailed] = useState(false)
  const [isConfigLoadFailed, setConfigLoadFailed] = useState(false)
  const [isFilterStateSettled, setFilterStateSettled] = useState(false)

  const loadPageIdRef = useRef(0)
  const shownListKeyRef = useRef(null)

  const isMountedRef = useIsMountedRef()

  const { handleAddErrorNotification } = useNotification()

  const { columns, handleLoadTableConfig, handleSetColumns, requiredColumns } =
    useTableConfigLoader({ entity })

  const {
    handleLoadFilterState,
    handleResetOrdering,
    handleSetOrdering,
    handleSetSize,
    ordering: filterStateOrdering,
  } = useFilterStateLoader({
    entity,
    handleChangeSize: setPageSize,
    pagination,
    sub: SUBMENU_DEFAULT_VALUE,
    // Old lists have no filters; a PUT with the `null` held before the read fails
    transformSaveFilters: getEmptyPermanentFilters,
  })

  const ordering = toOrderingList(filterStateOrdering)

  const tableListKey = getOldApiListTableKey({ listKey, ordering })

  function isLatestLoadPage(loadPageId) {
    return isOldApiListTableLatestLoad({
      isMounted: isMountedRef.current,
      latestLoadPageId: loadPageIdRef.current,
      loadPageId,
    })
  }

  function finishLoadPage() {
    setLoadingPage(false)
    setInitiated(true)
  }

  function handleLoadPageFail() {
    if (
      isOldApiListTableKeyShown({
        items,
        listKey: tableListKey,
        shownListKey: shownListKeyRef.current,
      })
    ) {
      handleAddErrorNotification('Error.CommonApiRequestFailKey')
    } else {
      setItems(null)
      setPageLoadFailed(true)
    }

    finishLoadPage()
  }

  function handleLoadPageError(error) {
    if (isOldApiListPageMissing({ error, pagination })) {
      setPagination(getOldApiListPreviousPagination(pagination), {
        replace: true,
      })
    } else {
      handleLoadPageFail()
    }
  }

  // Delayed callers (a sync success, mass actions) must load the current
  // page, so the latest closure is called. `false` skips the "Refreshed" toast.
  const handleLoadPage = useAsyncCallback(() => {
    loadPageIdRef.current += 1

    const loadPageId = loadPageIdRef.current

    setLoadingPage(true)

    return getList(getOldApiListParams({ ordering, pagination }))
      .then(({ data }) => {
        if (isLatestLoadPage(loadPageId)) {
          shownListKeyRef.current = tableListKey
          setItems(data.results.objects)
          setPageLoadFailed(false)
          setPagination(
            getOldApiListPagination({ meta: data.meta, pagination }),
            {
              replace: true,
            }
          )
          finishLoadPage()
          onLoad(data)
        }

        return data
      })
      .catch(error => {
        if (isLatestLoadPage(loadPageId)) {
          handleLoadPageError(error)
        }

        return false
      })
  })

  async function handleChangePageSize(pageSize) {
    setPagination(getOldApiListPageSizePagination({ pageSize, pagination }))

    return handleSetSize(pageSize)
  }

  async function handleLoadConfig() {
    return handleLoadTableConfig().then(config => {
      setConfigLoadFailed(!config)
    })
  }

  async function handleReload() {
    if (isConfigLoadFailed) {
      setConfigLoadFailed(false)
      handleLoadConfig()
    }

    return handleLoadPage()
  }

  useEffect(() => {
    handleLoadConfig()
    handleLoadFilterState().then(() => {
      setFilterStateSettled(true)
    })
  }, [])

  useEffect(() => {
    if (isFilterStateSettled) {
      handleLoadPage()
    }
  }, [isFilterStateSettled, pagination.page, pagination.pageSize, tableListKey])

  async function handleApplyConfig(config) {
    return updateOldApiListTableConfig({
      data: { columns, ...config },
      entity,
    })
  }

  function handleUpdateItem({ id, updates }) {
    setItems(prevItems =>
      updateOldApiListTableItem({
        id,
        items: prevItems,
        updates,
      })
    )
  }

  const isLoadFailed = isPageLoadFailed || isConfigLoadFailed
  const isRefetching = isInitiated && isLoadingPage

  return {
    columns,
    handleApplyConfig,
    handleChangePageSize,
    handleReload,
    handleResetOrdering,
    handleSetColumns,
    handleSetOrdering,
    handleUpdateItem,
    isLoadFailed,
    isLoading: isOldApiListTableLoading({ columns, isInitiated, isLoadFailed }),
    isRefetching,
    // Rows without a config cannot be drawn, so the load error is shown instead
    items: isConfigLoadFailed ? null : items,
    ordering,
    requiredColumns,
  }
}
